import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Alert } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getAccounts, switchAccount, removeAccount, Account, logout, getDid } from '../services/bluesky';
import { Theme, useThemedStyles, useTheme, useFontSize } from '../theme';
import { useTranslation } from 'react-i18next';

// Cache key prefixes for clearing cache
const CACHE_KEY_PREFIXES = ['cache_messages_', 'cache_cursor_'];

interface Props {
  onLogout: () => void;
  onAddAccount: () => void;
  onAccountSwitched: () => void;
  onSelectProfile: (did: string) => void;
  onOpenSaved: () => void;
  onOpenDrafts: () => void;
  onOpenTheme: () => void;
  onOpenLanguage: () => void;
  onOpenFontSize: () => void;
}

const SettingsItem: React.FC<{
  icon: any;
  title: string;
  subtitle?: string;
  onPress: () => void;
  titleColor?: string;
  showArrow?: boolean;
  iconType?: 'ionicons' | 'material';
}> = ({ icon, title, subtitle, onPress, titleColor, showArrow = true, iconType = 'ionicons' }) => (
  <SettingsItemInner 
    icon={icon}
    title={title}
    subtitle={subtitle}
    onPress={onPress}
    titleColor={titleColor}
    showArrow={showArrow}
    iconType={iconType}
  />
);

const SettingsItemInner: React.FC<{
  icon: any;
  title: string;
  subtitle?: string;
  onPress: () => void;
  titleColor?: string;
  showArrow?: boolean;
  iconType?: 'ionicons' | 'material';
}> = ({ icon, title, subtitle, onPress, titleColor, showArrow = true, iconType = 'ionicons' }) => {
  const { theme, themeName } = useTheme();
  const styles = useThemedStyles(createStyles);

  return (
    <TouchableOpacity style={styles.settingsItem} onPress={onPress}>
      <View style={styles.settingsItemIcon}>
        {iconType === 'ionicons' ? (
          <Ionicons name={icon} size={24} color={theme.colors.textMuted} />
        ) : (
          <MaterialCommunityIcons name={icon} size={24} color={theme.colors.textMuted} />
        )}
      </View>
      <View style={styles.settingsItemContent}>
        <Text style={[styles.settingsItemTitle, { color: titleColor || theme.colors.text }]}>{title}</Text>
        {subtitle ? <Text style={styles.settingsItemSubtitle}>{subtitle}</Text> : null}
      </View>
      {showArrow && <Ionicons name="chevron-forward" size={16} color={theme.colors.textMuted} />}
    </TouchableOpacity>
  );
};

const Settings: React.FC<Props> = ({ 
  onLogout, 
  onAddAccount, 
  onAccountSwitched,
  onSelectProfile,
  onOpenSaved,
  onOpenDrafts,
  onOpenTheme,
  onOpenLanguage,
  onOpenFontSize,
}) => {
  const insets = useSafeAreaInsets();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const currentDid = getDid();
  const { theme, themeName } = useTheme();
  const { fontSizeName } = useFontSize();
  const { t, i18n } = useTranslation();
  const styles = useThemedStyles(createStyles);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    const accs = await getAccounts();
    setAccounts(accs);
  };

  const handleSwitchAccount = async (did: string) => {
    if (loading) return;
    setLoading(true);
    try {
      await switchAccount(did);
      onAccountSwitched();
    } catch (e) {
      Alert.alert(t('common.error'), t('settings.switchAccountError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAccount = (did: string) => {
    Alert.alert(
      t('settings.deleteAccount'),
      t('settings.deleteAccountDesc'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        { 
          text: t('common.delete'), 
          style: 'destructive',
          onPress: async () => {
            await removeAccount(did);
            const accs = await getAccounts();
            if (accs.length === 0) {
              onLogout();
            } else {
              loadAccounts();
            }
          }
        }
      ]
    );
  };

  const handleLogout = async () => {
    await logout();
    onLogout();
  };

  const handleClearCache = () => {
    Alert.alert(
      t('settings.clearCacheTitle'),
      t('settings.clearCacheDesc'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        { 
          text: t('settings.clearCache'), 
          style: 'destructive',
          onPress: async () => {
            try {
              // Get all AsyncStorage keys
              const keys = await AsyncStorage.getAllKeys();
              // Filter keys for talk room messages only (cache_messages_ and cache_cursor_)
              const cacheKeys = keys.filter(key => 
                CACHE_KEY_PREFIXES.some(prefix => key.startsWith(prefix))
              );
              // Remove cache keys
              if (cacheKeys.length > 0) {
                await AsyncStorage.multiRemove(cacheKeys);
              }
              Alert.alert(t('common.info'), t('settings.clearCacheSuccess'));
            } catch (error) {
              console.error('Failed to clear cache:', error);
              Alert.alert(t('common.error'), t('settings.clearCacheError'));
            }
          }
        }
      ]
    );
  };

  const currentAccount = accounts.find(a => a.did === currentDid);

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('tabs.others')}</Text>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={{ paddingBottom: 40 }}>
        {/* Account Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('settings.account')}</Text>
          <View style={styles.sectionContent}>
            <SettingsItem 
              icon="person-outline" 
              title={t('settings.profile')} 
              subtitle={currentAccount ? `@${currentAccount.handle}` : t('settings.user')}
              onPress={() => currentDid && onSelectProfile(currentDid)}
            />
            
            {accounts.filter(a => a.did !== currentDid).map(acc => (
              <View key={acc.did} style={styles.accountSwitchRow}>
                <SettingsItem 
                  icon="swap-horizontal-outline" 
                  title={t('settings.switchAccount')} 
                  subtitle={`@${acc.handle}`}
                  onPress={() => handleSwitchAccount(acc.did)}
                />
                <TouchableOpacity onPress={() => handleRemoveAccount(acc.did)} style={styles.removeIcon}>
                  <Ionicons name="trash-outline" size={20} color={theme.colors.danger} />
                </TouchableOpacity>
              </View>
            ))}

            <SettingsItem 
              icon="person-add-outline" 
              title={t('settings.addAccount')} 
              onPress={onAddAccount}
            />
            
            <SettingsItem 
              icon="log-out-outline" 
              title={t('settings.logout')} 
              titleColor={theme.colors.danger}
              showArrow={false}
              onPress={handleLogout}
            />
          </View>
        </View>

        {/* General Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('common.settings')}</Text>
          <View style={styles.sectionContent}>
            <SettingsItem 
              icon="language-outline" 
              title={t('settings.language')} 
              subtitle={i18n.language === 'ja' ? t('settings.japanese') : t('settings.english')}
              onPress={onOpenLanguage}
            />
            <SettingsItem 
              icon="text-outline" 
              title={t('settings.fontSize')} 
              subtitle={fontSizeName === 'small' ? t('settings.small') : fontSizeName === 'large' ? t('settings.large') : t('settings.default')}
              onPress={onOpenFontSize}
            />
            <SettingsItem 
              icon="color-palette-outline" 
              title={t('settings.theme')} 
              subtitle={themeName === 'system' ? t('settings.system') : themeName === 'light' ? t('settings.light') : t('settings.dark')}
              onPress={onOpenTheme}
            />
            <SettingsItem 
              icon="storage-outline" 
              title={t('settings.storage')} 
              subtitle={t('settings.storageDesc')}
              iconType="material"
              onPress={handleClearCache}
            />
          </View>
        </View>

        {/* App Settings Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('settings.appSettings')}</Text>
          <View style={styles.sectionContent}>
            <SettingsItem 
              icon="bookmark-outline" 
              title={t('savedPosts.title')} 
              onPress={onOpenSaved}
            />
            <SettingsItem 
              icon="document-text-outline" 
              title={t('drafts.title')} 
              onPress={onOpenDrafts}
            />
          </View>
        </View>

        {/* Support Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('settings.support')}</Text>
          <View style={styles.sectionContent}>
            <SettingsItem 
              icon="information-circle-outline" 
              title={t('settings.appInfo')} 
              subtitle={t('settings.version')}
              onPress={() => {
                Alert.alert(
                  t('settings.appInfo'),
                  `${t('settings.version')}\n\nSKyscraper_RN\nBluesky Client for React Native`,
                  [{ text: 'OK' }]
                );
              }}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    height: 50,
    backgroundColor: theme.colors.card,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  content: {
    flex: 1,
  },
  section: {
    marginTop: 24,
  },
  sectionTitle: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginLeft: 16,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  sectionContent: {
    backgroundColor: theme.colors.card,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: theme.colors.border,
  },
  settingsItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: theme.colors.card,
  },
  settingsItemIcon: {
    width: 32,
    alignItems: 'center',
    marginRight: 12,
  },
  settingsItemContent: {
    flex: 1,
  },
  settingsItemTitle: {
    fontSize: 16,
    color: theme.colors.text,
  },
  settingsItemSubtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    marginTop: 2,
  },
  accountSwitchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.card,
  },
  removeIcon: {
    padding: 12,
    paddingRight: 16,
  },
});

export default Settings;
